import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import { useNavigate } from 'react-router-dom';
import 'swiper/css';
import '../../css/Common-components/titleBar.css';
import '../../css/Home/promotionBanner.css';
import { ReactComponent as ArrowLeftIcon } from '../../assets/icons/svg/arrowLeft.svg';

const DEVICE_BREAKPOINTS = {
  mobile: 651,
  tablet: 1327,
};

const DEVICE_LAYOUTS = {
  PC: { minWidth: 368, gap: 24 },
  TA: { minWidth: 280, gap: 16 },
  MO: { minWidth: 1, gap: 12 },
};
const MAX_VISIBLE_PROMO_BANNERS = 3;

const EMPTY_BANNERS = { PC: [], TA: [], MO: [] };

const PromotionBanner = ({ promoBanners = EMPTY_BANNERS, promoStatus = {}, title = '공지사항', className = '' }) => {
  const navigate = useNavigate();
  const bannerRef = useRef(null);
  const swiperRef = useRef(null);
  const [device, setDevice] = useState('PC');
  const [bannerWidth, setBannerWidth] = useState(0);

  useEffect(() => {
    const detectDevice = () => {
      if (typeof window === 'undefined') return;
      const width = window.innerWidth;
      if (width <= DEVICE_BREAKPOINTS.mobile) {
        setDevice('MO');
      } else if (width <= DEVICE_BREAKPOINTS.tablet) {
        setDevice('TA');
      } else {
        setDevice('PC');
      }
    };

    detectDevice();
    window.addEventListener('resize', detectDevice);
    return () => window.removeEventListener('resize', detectDevice);
  }, []);

  useEffect(() => {
    const node = bannerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver(([entry]) => {
      const nextWidth = entry?.contentRect?.width || 0;
      setBannerWidth(nextWidth);
    });

    resizeObserver.observe(node);
    return () => resizeObserver.disconnect();
  }, []);

  const activeBanners = useMemo(() => {
    const source = resolvePromoSourceByDevice(promoBanners, device);
    if (!Array.isArray(source)) return [];
    return source.map(normalizePromoItem).filter(Boolean);
  }, [promoBanners, device]);

  const { loading: isLoading = false, error: promoError = null } = promoStatus || {};
  const layout = DEVICE_LAYOUTS[device] || DEVICE_LAYOUTS.PC;
  const visibleCount = calculateVisibleCount(bannerWidth, layout, device);
  const canSwipe = activeBanners.length > visibleCount;

  const fallbackText = isLoading
    ? `${title} 배너 불러오는 중...`
    : promoError
      ? `${title} 배너를 불러오지 못했습니다.`
      : `${title} 배너`;

  // console.log('전체 프로모션 배너:', promoBanners);
  // console.log('동작환경:', device);
  // console.log('status:', promoStatus);
  // console.log('활성화된 배너:', activeBanners);

  const handlePromoClick = (item) => {
    const href = String(item?.href || '').trim();
    if (!href) return;
    if (item?.isExternal || /^https?:\/\//i.test(href)) {
      window.location.assign(href);
      return;
    }
    navigate(href);
  };

  const handleSlidePrev = () => {
    swiperRef.current?.slidePrev();
  };

  const handleSlideNext = () => {
    swiperRef.current?.slideNext();
  };

  return (
    <div className={['promotion-banner-wrapper', className].filter(Boolean).join(' ')}>
      <div className='promotion-banner-title-row'>
        <h2 className='section-title'>{title}</h2>
      </div>
      <div className='promotion-banner' ref={bannerRef}>
        {activeBanners.length > 0 && !isLoading ? (
          <>
            <Swiper
              key={`${device}-${visibleCount}-${activeBanners.length}-${canSwipe ? 'loop' : 'static'}`}
              className='promotion-banner-swiper'
              modules={[Autoplay]}
              slidesPerView={visibleCount}
              slidesPerGroup={1}
              spaceBetween={layout.gap}
              allowTouchMove={canSwipe}
              loop={canSwipe}
              autoplay={canSwipe ? {
                delay: 5000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              } : false}
              watchOverflow
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
            >
              {activeBanners.map((item, idx) => (
                <SwiperSlide
                  key={`${device}-promotion-${idx}`}
                  className='promotion-banner-slide'
                >
                  <PromoBannerFrame
                    item={item}
                    index={idx}
                    device={device}
                    onClick={handlePromoClick}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            {canSwipe && (
              <>
                <button
                  type='button'
                  className='promotion-banner-nav promotion-banner-nav-prev'
                  aria-label='이전 프로모션 배너'
                  onClick={handleSlidePrev}
                >
                  <ArrowLeftIcon />
                </button>
                <button
                  type='button'
                  className='promotion-banner-nav promotion-banner-nav-next'
                  aria-label='다음 프로모션 배너'
                  onClick={handleSlideNext}
                >
                  <ArrowLeftIcon />
                </button>
              </>
            )}
          </>
        ) : (
          <span className='promotion-banner-empty'>
            {fallbackText}
          </span>
        )}
      </div>
    </div>
  );
};

export default PromotionBanner;

function calculateVisibleCount(containerWidth, layout, device) {
  if (device === 'MO') return 1;

  const width = Number(containerWidth);
  const minWidth = Number(layout?.minWidth) || 1;
  const gap = Number(layout?.gap) || 0;
  if (!Number.isFinite(width) || width <= 0) return 1;

  const fitCount = Math.floor((width + gap) / (minWidth + gap));
  return Math.max(1, Math.min(fitCount, MAX_VISIBLE_PROMO_BANNERS));
}

function PromoBannerFrame({ item, index, device, onClick }) {
  const image = (
    <img
      src={item.src}
      alt={`promotion-${device}-${index}`}
      loading={index === 0 ? 'eager' : 'lazy'}
      decoding='async'
    />
  );

  if (item.href) {
    return (
      <button
        type='button'
        className='promotion-banner-frame is-clickable'
        onClick={() => onClick(item)}
      >
        {image}
      </button>
    );
  }

  return (
    <div className='promotion-banner-frame'>
      {image}
    </div>
  );
}

function normalizeBoardType(value) {
  const type = String(value || '').toLowerCase();
  if (type === 'notice' || type === 'event') return type;
  return '';
}

function buildInternalHref(boardTypeRaw, postIdRaw) {
  const boardType = normalizeBoardType(boardTypeRaw);
  const postId = Number(postIdRaw);
  if (!boardType) return null;
  if (Number.isFinite(postId) && postId > 0) {
    return `/${boardType}/${postId}`;
  }
  return `/${boardType}`;
}

function normalizePromoItem(item) {
  if (typeof item === 'string') {
    return { src: item, href: null, isExternal: false };
  }
  if (!item || typeof item !== 'object') return null;

  const src = String(item.src || item.img_url || '').trim();
  if (!src) return null;

  let href = String(item.href || '').trim();
  if (!href) {
    const url = String(item.link_url || '').trim();
    if (url) {
      href = url;
    } else {
      href = buildInternalHref(item.link_board_type, item.link_post_id) || '';
    }
  }

  const isExternal = Boolean(Number(item.is_external)) || /^https?:\/\//i.test(href);
  return { src, href: href || null, isExternal };
}

function resolvePromoSourceByDevice(promoBanners, device) {
  const current = Array.isArray(promoBanners?.[device]) ? promoBanners[device] : [];
  if (device !== 'TA' || current.length >= 2) {
    return current;
  }

  const web = Array.isArray(promoBanners?.PC) ? promoBanners.PC : [];
  if (web.length >= 2) {
    return web;
  }

  return current;
}
